/**
 * Scroll a source's cited line span or HTML snippet into view and flash a transient highlight.
 *
 * For plain-text sources, the source viewer ({@link SourceViewer}) renders one
 * `<tr data-source-line="N">` per physical line. When `lines` is non-empty this hook:
 * 1. Finds the row for the first cited line by `[data-source-line="${min}"]`.
 *    Because a reveal usually races a tab-switch + Sources-subtab mount + async
 *    source-body fetch (the rows may not be in the DOM yet), the lookup RETRIES
 *    on a bounded poll until the row appears, then proceeds. If it never appears
 *    it gives up cleanly (no flash).
 * 2. Scrolls it into view (smooth, center).
 * 3. Adds the `.source-line-flash` class to every cited line's row and removes
 *    them after 2s.
 *
 * For HTML sources (`[data-testid="source-body-html"]`) where line rows are absent,
 * when `lines` is empty and `snippet` is provided, this hook locates the most
 * specific matching element containing the verbatim cited text, scrolls it into
 * view, and flashes it with `.source-line-flash`.
 *
 * The effect re-runs when `lines` or `snippet` changes. The caller
 * ({@link SourceViewer}) memoizes line arrays on `(reveal, body)`, so it is stable
 * across unrelated re-renders but fresh on each reveal (each reveal carries a new
 * target object) — repeat reveals of the same span therefore still re-flash.
 * Mirrors {@link useScrollToHighlight} (the Blueprint-card analogue). Nothing is
 * logged (HIPAA).
 */
import { useEffect } from "react";

/** How often (ms) to re-check the DOM for the target line row. */
const POLL_INTERVAL_MS = 100;
/** How long (ms) to keep polling before giving up on a missing row. */
const POLL_WINDOW_MS = 2000;
/** How long (ms) the flash stays applied once the rows are found. */
const FLASH_DURATION_MS = 2000;

/** Locate a source line row by its 1-based physical line number. */
function findRow(lineNo: number): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-source-line="${lineNo}"]`);
}

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Locate a matching element within a rendered source body container.
 *
 * Traverses elements within `container` (`[data-testid="source-body-prose"]`
 * or `[data-testid="source-body-html"]`) to find the most specific element containing
 * the snippet, matching sub-phrase sliding windows, or containing the element text.
 */
function findHtmlElement(
  container: HTMLElement,
  snippet?: string,
): HTMLElement | null {
  const target = snippet?.trim();
  if (!target) return null;

  const plainTarget =
    target
      .replace(/<[^>]+>/g, "")
      .replace(/\[\[(?:src|inf|cite):[^\]]*\]\]/g, "")
      .replace(/(\*\*|__|\*|_|~~)/g, "")
      .trim() || target;
  const cleanedTarget = plainTarget
    .replace(/^(\.\.\.|…)\s*/, "")
    .replace(/\s*(\.\.\.|…)$/, "")
    .trim();

  const candidates = Array.from(
    new Set([target, plainTarget, cleanedTarget].map(normalizeWhitespace)),
  ).filter((c) => c.length > 0);

  const elements = Array.from(container.querySelectorAll<HTMLElement>("*"));
  // Find matching elements containing the target text
  const matching = elements.filter((el) => {
    const elText = normalizeWhitespace(el.textContent ?? "");
    return (
      candidates.some((c) => elText.includes(c)) &&
      // Skip elements whose children also fully contain the target text (to pick the most specific child)
      !Array.from(el.children).some((child) => {
        const childText = normalizeWhitespace(child.textContent ?? "");
        return candidates.some((c) => childText.includes(c));
      })
    );
  });

  if (matching.length > 0) return matching[0];

  // If cleanedTarget is multi-word, attempt matching sub-phrase windows (consecutive word runs >= 3)
  if (cleanedTarget.length > 0) {
    const words = normalizeWhitespace(cleanedTarget)
      .split(" ")
      .filter((w) => w.length > 0);
    if (words.length >= 3) {
      for (let len = words.length - 1; len >= 3; len--) {
        for (let start = 0; start <= words.length - len; start++) {
          const subPhrase = words.slice(start, start + len).join(" ");
          const subMatching = elements.filter((el) => {
            const elText = normalizeWhitespace(el.textContent ?? "");
            return (
              elText.includes(subPhrase) &&
              !Array.from(el.children).some((child) => {
                const childText = normalizeWhitespace(child.textContent ?? "");
                return childText.includes(subPhrase);
              })
            );
          });
          if (subMatching.length > 0) return subMatching[0];
        }
      }
    }
  }

  // If a multi-element snippet contains a specific element, match the element whose text is within candidate
  const contained = elements.filter((el) => {
    const elText = normalizeWhitespace(el.textContent ?? "");
    if (elText.length < 10) return false;
    return (
      candidates.some((c) => c.includes(elText)) &&
      !Array.from(el.children).some((child) => {
        const childText = normalizeWhitespace(child.textContent ?? "");
        return (
          childText.length >= 10 &&
          candidates.some((c) => c.includes(childText))
        );
      })
    );
  });
  if (contained.length > 0) return contained[0];

  return null;
}

/**
 * Scroll to and flash-highlight a source's cited line span, prose element, or HTML snippet.
 *
 * @param lines - The 1-based physical line numbers to scroll to + flash, or an
 *   empty array when there is nothing to highlight (e.g. an unlocatable stale
 *   citation — the caller still shows a banner; this hook just no-ops, or for
 *   search snippet reveals). Must be a referentially-stable array (memoized by
 *   the caller) so the effect only re-runs when the span actually changes.
 * @param snippet - Optional verbatim cited text or search result snippet for locating
 *   matching elements in prose or HTML views where line rows are absent.
 * @param revealKey - Optional reveal key token so repeat reveals re-trigger highlight.
 */
export function useScrollToSourceLine(
  lines: number[],
  snippet?: string,
  revealKey?: unknown,
): void {
  useEffect(() => {
    if (lines.length === 0 && !snippet) return undefined;
    const sorted = [...lines].sort((a, b) => a - b);
    let flashTimer: number | undefined;
    let pollTimer: number | undefined;
    let flashed: HTMLElement[] = [];
    const deadline = Date.now() + POLL_WINDOW_MS;

    /** Scroll to target element and flash it, then clean up. */
    const reveal = (first: HTMLElement, targets: HTMLElement[]): void => {
      first.scrollIntoView({ behavior: "smooth", block: "center" });
      flashed = targets;
      for (const el of flashed) el.classList.add("source-line-flash");
      flashTimer = window.setTimeout(() => {
        for (const el of flashed) el.classList.remove("source-line-flash");
      }, FLASH_DURATION_MS);
    };

    /** Poll the DOM for the target row, prose element, or HTML element until it appears or time runs out. */
    const attempt = (): void => {
      if (sorted.length > 0) {
        const el = findRow(sorted[0]);
        if (el) {
          const rows = sorted
            .map(findRow)
            .filter((item): item is HTMLElement => item !== null);
          reveal(el, rows);
          return;
        }
      }

      // If line rows are absent or sorted is empty, check prose or HTML container with snippet
      const proseContainer = document.querySelector<HTMLElement>(
        '[data-testid="source-body-prose"]',
      );
      if (proseContainer) {
        const proseEl = findHtmlElement(proseContainer, snippet);
        if (proseEl) {
          reveal(proseEl, [proseEl]);
          return;
        }
      }

      const htmlContainer = document.querySelector<HTMLElement>(
        '[data-testid="source-body-html"]',
      );
      if (htmlContainer) {
        const htmlEl = findHtmlElement(htmlContainer, snippet);
        if (htmlEl) {
          reveal(htmlEl, [htmlEl]);
          return;
        }
      }

      if (Date.now() >= deadline) return;
      pollTimer = window.setTimeout(attempt, POLL_INTERVAL_MS);
    };

    attempt();

    return () => {
      if (flashTimer !== undefined) window.clearTimeout(flashTimer);
      if (pollTimer !== undefined) window.clearTimeout(pollTimer);
      for (const el of flashed) el.classList.remove("source-line-flash");
    };
  }, [lines, snippet, revealKey]);
}
